import React, { useEffect, useState } from "react";
import styles from "./createPost.module.css";
import {
  FaTimes,
  FaImage,
  FaVideo,
  FaCalendarAlt,
  FaRegFileAlt,
  FaSmile,
} from "react-icons/fa";
import {
  useCreatePostMutation,
  useUpdatePostMutation,
} from "../store/api/postApi";
import { useGetPostsQuery } from "../store/api/postApi";
import { toast } from "react-toastify";

export default function CreatePostModal({
  isOpen,
  onClose,
  userPhoto,
  userName,
  isEditing,
  existingPost,
}) {
  const [postContent, setPostContent] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [createPost, { isLoading }] = useCreatePostMutation();
  const [updatePost, { isLoading: isUpdating }] = useUpdatePostMutation();
  const { refetch } = useGetPostsQuery();

  useEffect(() => {
    if (isEditing && existingPost) {
      setPostContent(existingPost.caption || "");
      setPreviewUrls(existingPost.images || []);
      setVisibility(existingPost.visibility ?? 0);
    }
  }, [isEditing, existingPost]);
  const [visibility, setVisibility] = useState(0); // Default = Public
  if (!isOpen) return null;

  const handleFileSelect = (e, fileType) => {
    const files = Array.from(e.target.files);

    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    const videoFiles = files.filter((file) => file.type.startsWith("video/"));

    const existingImages = mediaFiles.filter((file) =>
      file.type.startsWith("image/"),
    ).length;
    const existingVideos = mediaFiles.filter((file) =>
      file.type.startsWith("video/"),
    ).length;

    // Image validation
    if (existingImages + imageFiles.length > 4) {
      toast.error("You can upload maximum 4 photos.");
      return;
    }

    // Video validation
    if (existingVideos + videoFiles.length > 1) {
      toast.error("You can upload only 1 video.");
      return;
    }

    // Create preview URLs
    const newPreviewUrls = files.map((file) => URL.createObjectURL(file));

    setMediaFiles((prev) => [...prev, ...files]);
    setPreviewUrls((prev) => [...prev, ...newPreviewUrls]);
  };

  const removeFile = (index) => {
    // Revoke the preview URL to prevent memory leaks
    URL.revokeObjectURL(previewUrls[index]);

    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!postContent.trim() && mediaFiles.length === 0) {
      toast.error("Please add content or media to your post");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("Caption", postContent);
      formData.append("Visibility", visibility);

      // Append all media files
      mediaFiles.forEach((file) => {
        formData.append("MediaFiles", file);
      });

      await createPost(formData).unwrap();

      toast.success("Post created successfully");

      // Clean up preview URLs
      previewUrls.forEach((url) => URL.revokeObjectURL(url));

      // Reset form
      setPostContent("");
      setMediaFiles([]);
      setPreviewUrls([]);
      onClose();
    } catch (error) {
      console.error("Error creating post:", error);
      toast.error("Failed to create post. Please try again.");
    }
  };

  const handleUpdatePost = async (postId, postContent, mediaFiles) => {
    if (!postContent.trim() && mediaFiles.length === 0) {
      toast.error("Please add content or media to your post");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("Caption", postContent);

      // Append all media files
      mediaFiles.forEach((file) => {
        formData.append("MediaFiles", file);
      });

      formData.append("Visibility", visibility);
      await updatePost({ postId, formData }).unwrap();

      // Clean up preview URLs
      previewUrls.forEach((url) => URL.revokeObjectURL(url));

      // Reset form
      setPostContent("");
      setMediaFiles([]);
      setPreviewUrls([]);
      onClose();
      refetch();
    } catch (error) {
      console.error("Error creating post:", error);
      toast.error("Failed to update post. Please try again.");
    }
  };

  const initials = userName
    ?.split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalHeaderTitle}>Create a post</h2>
          <button
            onClick={onClose}
            className={styles.closeBtn}
            disabled={isLoading}
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* User Info */}
        <div className={styles.modalUserInfo}>
          {userPhoto ? (
            <img src={userPhoto} alt={userName} className={styles.userPhoto} />
          ) : (
            <div className={styles.noPhoto}>{initials || "?"}</div>
          )}
          <div>
            <div className={styles.userName}>{userName}</div>
            <select
              className={styles.visibilitySelect}
              value={visibility}
              onChange={(e) => setVisibility(Number(e.target.value))}
            >
              <option value={0}>Anyone</option>
              <option value={1}>Connections only</option>
              <option value={2}>Only me</option>
            </select>
          </div>
        </div>

        {/* Content Area */}
        <div className={styles.modalTextareaContainer}>
          <textarea
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            placeholder="What do you want to talk about?"
            autoFocus
            className={styles.modalTextarea}
            disabled={isLoading}
          />
        </div>

        {/* Media Preview */}
        {previewUrls.length > 0 && (
          <div className={styles.mediaPreviewContainer}>
            {previewUrls.map((url, index) => (
              <div key={index} className={styles.mediaPreviewItem}>
                {mediaFiles[index].type.startsWith("image/") ? (
                  <img
                    src={url}
                    alt={`Preview ${index + 1}`}
                    className={styles.previewImage}
                  />
                ) : (
                  <video src={url} className={styles.previewVideo} controls />
                )}
                <button
                  onClick={() => removeFile(index)}
                  className={styles.removeMediaBtn}
                  disabled={isLoading}
                >
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Media Options */}
        <div className={styles.modalMediaOptions}>
          <label className={styles.mediaBtn}>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={(e) => handleFileSelect(e, "image")}
              style={{ display: "none" }}
              disabled={isLoading}
            />
            <FaImage /> Photo / Video
          </label>

          {/* <label className={styles.mediaBtn}>
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => handleFileSelect(e, 'video')}
              style={{ display: 'none' }}
              disabled={isLoading}
            />
            <FaVideo /> Video
          </label> */}

          {/* <button className={styles.mediaBtn} disabled={isLoading}>
            <FaCalendarAlt /> Event
          </button> */}

          {/* <button className={styles.mediaBtn} disabled={isLoading}>
            <FaRegFileAlt /> Article
          </button> */}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          {/* <button className={styles.emojiBtn} disabled={isLoading}>
            <FaSmile size={22} />
          </button> */}
          <button
            onClick={handleSubmit}
            disabled={!postContent.trim() || isLoading}
            className={`${styles.postBtn} ${
              !postContent.trim() || isLoading ? styles.disabled : ""
            }`}
          >
            {isLoading ? "Posting..." : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}
